import { CloseButton, P2GBox, P2GContainer, P2GDrawer, useP2GMediaQueryDown } from '@parcel2go/core-ui';
import { useAuth } from 'react-oidc-context';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import {
  BasketItem,
  BasketQuote,
  QuoteAddress,
  QuoteError,
  QuoteResponse,
  QuoteExtras
} from '~/lib/types/basket-types';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { BasketSections } from '~/lib/types/enums';
import Quotes from '~/components/quote-results/quotes';
import { capturePromiseException } from '~/lib/metrics';
import NoResults from './message/no-results';
import ServiceSwitchWrapper from './service-switch-wrapper';
import LoadingMessage from './message/loading';
import { QuoteFilterOptions } from '~/lib/quotes';

type ServiceSwitcherCoreProps = {
  open: boolean;
  onClose: () => void;
  changeService: (quote: BasketQuote, extras: string[]) => Promise<boolean>;
  item: BasketItem;
  quoteUrl: string;
  errorMessages?: QuoteError[];
  children?: React.ReactNode;
  onSelect?: () => void;
  onGetQuote?: () => void;
  quoteDefaultFilter?: QuoteFilterOptions;
  dataTestId?: string;
  maxHeight?: ComponentProps<typeof P2GBox>['maxHeight'];
  height?: ComponentProps<typeof P2GBox>['height'];
  intent?: 'INVALID' | 'SWITCH';
};

export default function ServiceSwitcherCore({
  open,
  onClose,
  item,
  quoteUrl,
  changeService,
  onGetQuote,
  errorMessages,
  onSelect,
  quoteDefaultFilter,
  height,
  maxHeight,
  dataTestId,
  children,
  intent
}: ServiceSwitcherCoreProps) {
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session, track } = useAnalytics();
  const isSmall = useP2GMediaQueryDown('md');
  const [data, setData] = useState<QuoteResponse | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getData = async (url: string) => {
      setData(undefined);
      setIsLoading(true);

      const res: QuoteResponse = await authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, url)
        .then((res) => res.json())
        .catch(capturePromiseException);

      setData(res);
      setIsLoading(false);
    };

    if (open && quoteUrl) {
      getData(quoteUrl);
    }
  }, [open, quoteUrl]);

  const postcode = useMemo(() => (item.sections[BasketSections.ORIGIN]?.payload as QuoteAddress)?.postcode, [item]);

  const currentSelectedExtras = useMemo(() => {
    const extrasPayload = item.sections[BasketSections.EXTRAS]?.payload as QuoteExtras | undefined;
    return extrasPayload?.extras || [];
  }, [item]);

  const quotes: BasketQuote[] = useMemo(() => {
    if (!data || !data.result) return [];
    return data.result.map((quote) => {
      return {
        ...quote,
        currency: data.currency
      };
    });
  }, [data]);

  const onSelectService = useCallback(
    async (_index: number, quote: BasketQuote, extras: string[]) => {
      onClose();
      if (await changeService(quote, extras)) {
        track({
          type: 'service_switched',
          payload: { quote: quote, extras: extras }
        });
      }

      onSelect?.();
    },
    [changeService, onClose, onSelect, track]
  );

  const noResults = !quotes || quotes.length === 0;
  const hideFilter = isSmall || quotes.length < 5;

  return (
    <P2GDrawer open={open} onClose={onClose} anchor="bottom" dataTestId="service-switcher">
      <P2GBox height={height} maxHeight={maxHeight} dataTestId={dataTestId}>
        <P2GContainer maxWidth="lg">
          <P2GBox mt={2} mb={2} display="flex" justifyContent="end">
            <CloseButton closeButtonText={'Close'} handleOnClose={onClose} />
          </P2GBox>
          {children && <P2GBox mb={2}>{children}</P2GBox>}
          {isLoading ? (
            <LoadingMessage onClose={onClose} />
          ) : noResults ? (
            <NoResults onGetQuote={onGetQuote} errorMessages={errorMessages} onClose={onClose} />
          ) : (
            <ServiceSwitchWrapper
              intent={intent}
              onClose={onClose}
              errorMessages={errorMessages}
              quotes={
                <Quotes
                  quoteId={quoteUrl}
                  disableStickyFilter={true}
                  hideFilter={hideFilter}
                  quotes={quotes}
                  postcode={postcode}
                  onSelect={onSelectService}
                  loading={isLoading}
                  defaultFilter={hideFilter ? undefined : quoteDefaultFilter}
                  hideLastUsed={true}
                  currentSelectedExtras={currentSelectedExtras}
                />
              }
            />
          )}
        </P2GContainer>
      </P2GBox>
    </P2GDrawer>
  );
}
